<script lang="ts">
  /**
   * TaskGraph — live canonical task graph inside MU.
   *
   * Overview = progress-sorted heatmap grid of all tracks (cross-track
   * dependency arrows over it); click a track → vertical milestone DAG
   * (dagre TB) coloured by state with sequence + dependency edges; click a
   * milestone → side panel with its live NEXUS related-memories.
   *
   * Data is read-only and polled (signature poll → refetch on change).
   */
  import { onMount } from 'svelte'
  import cytoscape from 'cytoscape'
  import dagre from 'cytoscape-dagre'
  import {
    appView, taskGraph, milestonePanel, connectionPanel,
    loadTaskGraph, refreshIfChanged, selectMilestone, closeMilestonePanel, closeConnectionPanel,
    loadTree, treeState, expansion, visibleSubtree, toggleExpand, isExpandable,
  } from './taskGraphStore.svelte'
  import { fetchCodexGraph, type TaskGraphData, type TaskMilestone, type CodexGraph } from './api.svelte'
  import GanttView from './GanttView.svelte'
  import SankeyView from './SankeyView.svelte'

  cytoscape.use(dagre as never)

  const STATE_ORDER = ['done', 'active', 'next', 'waiting', 'planned'] as const
  const COLOR: Record<string, string> = {
    done: '#3fb950', active: '#58a6ff', next: '#d29922', waiting: '#db6d28', planned: '#5b6472',
  }
  /** Colour for a decomposition tree node: a sub-process (way) reads as a
   *  procedure; a step (node) reads by its state, falling back to grey. */
  function treeNodeColor(kind: string, state: string | null): string {
    if (kind === 'way') return '#8957e5'
    if (kind === 'node') return COLOR[state ?? ''] || '#5b6472'
    return '#5a6bd8'
  }

  let cyContainer: HTMLDivElement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cy: any = null
  let mode: 'overview' | 'track' = $state('overview')
  let viewKind: 'graph' | 'gantt' | 'sankey' = $state('graph')
  let curSlug: string | null = $state(null)
  let crumb = $state('')
  let activeStates = $state(new Set<string>(STATE_ORDER))
  let search = $state('')
  let railOpen = $state(true)
  let pollTimer: ReturnType<typeof setInterval> | null = null

  const NARROW = 760
  function isNarrow(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < NARROW
  }
  /** Collapse/expand the tracks flyout WITHOUT leaving the TASKS view.
   *  Resize the canvas afterwards so it reclaims/yields the freed width
   *  (desktop, where the rail is in-flow; on mobile the rail overlays). */
  function toggleRail(): void {
    railOpen = !railOpen
    setTimeout(() => { if (cy) cy.resize() }, 60)
  }

  type TrackModel = {
    way_id: number; slug: string; name: string; refs: number[]
    c: Record<string, number>; pct: number
  }
  type Model = {
    M: Map<number, TaskMilestone>
    tracks: TrackModel[]
    bySlug: Map<string, TrackModel>
    primary: Map<number, string>
    depEdges: TaskGraphData['deps']
    totals: Record<string, number>
    totalsPct: number
  }

  function buildModel(data: TaskGraphData): Model {
    const M = new Map<number, TaskMilestone>(data.milestones.map((m) => [m.id, m]))
    const bySlug = new Map<string, TrackModel>()
    const tracks: TrackModel[] = data.tracks.map((t) => {
      const refs = Array.isArray(t.node_refs) ? t.node_refs : []
      const c: Record<string, number> = { done: 0, active: 0, next: 0, waiting: 0, planned: 0, total: 0 }
      for (const id of refs) {
        const m = M.get(id); if (!m) continue
        c.total++; const s = m.state || 'planned'; c[s] = (c[s] || 0) + 1
      }
      const pct = c.total ? Math.round((100 * c.done) / c.total) : 0
      const tt: TrackModel = { way_id: t.way_id, slug: t.slug, name: t.name, refs, c, pct }
      bySlug.set(t.slug, tt)
      return tt
    })
    const primary = new Map<number, string>()
    for (const m of data.milestones) if (m.project && bySlug.has(m.project)) primary.set(m.id, m.project)
    for (const t of tracks) for (const id of t.refs) if (!primary.has(id)) primary.set(id, t.slug)
    const depEdges = data.deps.filter((d) => d.relation_type === 'depends_on' && d.from_id && d.to_id)
    const distinct = new Map<number, string>()
    for (const t of tracks) for (const id of t.refs) { const m = M.get(id); if (m) distinct.set(id, m.state || 'planned') }
    const totals: Record<string, number> = { done: 0, active: 0, next: 0, waiting: 0, planned: 0, total: 0 }
    distinct.forEach((s) => { totals.total++; totals[s] = (totals[s] || 0) + 1 })
    const totalsPct = totals.total ? Math.round((100 * totals.done) / totals.total) : 0
    return { M, tracks, bySlug, primary, depEdges, totals, totalsPct }
  }

  const model = $derived(taskGraph.data ? buildModel(taskGraph.data) : null)
  const railTracks = $derived(model ? [...model.tracks].sort((a, b) => b.pct - a.pct) : [])
  const spotTotal = $derived<Record<string, number> | null>(model ? { ...model.totals, pct: model.totalsPct } : null)
  const spotClaims = $derived(model?.bySlug.get('claims-proof'))
  const spotFlow = $derived(model?.bySlug.get('flow-runtime-evolution'))

  function pctColor(p: number): string {
    const mix = (c1: number[], c2: number[], k: number) =>
      `rgb(${c1.map((v, i) => Math.round(v + (c2[i] - v) * k)).join(',')})`
    const a = p / 100
    if (a < 0.5) return mix([248, 81, 73], [210, 153, 34], a / 0.5)
    return mix([210, 153, 34], [63, 185, 80], (a - 0.5) / 0.5)
  }

  function segStyle(c: Record<string, number>): { s: string; w: number }[] {
    const t = c.total || 1
    return STATE_ORDER.map((s) => ({ s, w: (100 * (c[s] || 0)) / t }))
  }

  function runLayout(lo: { one: (e: string, f: () => void) => void; run: () => void }, finish?: () => void) {
    const fin = finish || (() => cy.fit(undefined, 40))
    try { lo.one('layoutstop', fin) } catch { /* ignore */ }
    lo.run()
    setTimeout(fin, 60)
  }
  function fitReadable(min: number, max: number) {
    cy.fit(undefined, 30)
    const z = Math.max(min, Math.min(max, cy.zoom()))
    cy.zoom(z)
    const bb = cy.elements().boundingBox()
    cy.pan({ x: cy.width() / 2 - (bb.x1 + bb.w / 2) * z, y: 34 - bb.y1 * z })
  }

  function buildOverview() {
    if (!cy || !model) return
    mode = 'overview'; curSlug = null; crumb = `${model.tracks.length} tracks`
    const els: Record<string, unknown>[] = []
    for (const t of model.tracks) {
      els.push({
        data: {
          id: 't_' + t.slug, label: `${t.slug}\n${t.pct}% · ${t.c.done}/${t.c.total}`,
          col: pctColor(t.pct), slug: t.slug, sortKey: 1000 - t.pct * 10 - t.c.total,
        }, classes: 'track',
      })
    }
    const seen = new Set<string>()
    for (const d of model.depEdges) {
      const a = model.primary.get(d.from_id), b = model.primary.get(d.to_id)
      if (!a || !b || a === b) continue
      const k = a + '>' + b; if (seen.has(k)) continue; seen.add(k)
      els.push({ data: { id: 'e_' + k, source: 't_' + a, target: 't_' + b }, classes: 'dep' })
    }
    cy.elements().remove(); cy.add(els)
    runLayout(cy.layout({
      name: 'grid', animate: false, padding: 34, avoidOverlap: true, condense: false,
      cols: 6, sort: (a: { data: (k: string) => number }, b: { data: (k: string) => number }) => a.data('sortKey') - b.data('sortKey'),
    }))
    applyStateFilter()
  }

  function focusTrack(slug: string) {
    if (!cy || !model) return
    const t = model.bySlug.get(slug); if (!t) return
    mode = 'track'; curSlug = slug; crumb = t.name
    // On mobile the rail overlays the canvas — get out of the way so the
    // freshly-opened DAG is visible. Resize after the rail animates out.
    if (isNarrow() && railOpen) { railOpen = false; setTimeout(() => { if (cy) cy.resize() }, 60) }
    const inTrack = new Set(t.refs)
    const els: Record<string, unknown>[] = []
    const added = new Set<number>()
    const addMs = (id: number, ext: boolean) => {
      if (added.has(id)) return; added.add(id)
      const m = model!.M.get(id); if (!m) return
      const expandable = !ext && isExpandable('n:' + id)
      const open = expansion.open.has('n:' + id)
      const lbl = (expandable ? (open ? '▾ ' : '▸ ') : '') + (m.title || ('#' + id))
      els.push({
        data: {
          id: 'm_' + id, mid: id, label: lbl, col: COLOR[m.state || 'planned'] || COLOR.planned,
          h: Math.max(40, 22 + Math.ceil(lbl.length / 30) * 15), state: m.state || 'planned',
          expandable: expandable ? 1 : 0,
        }, classes: 'ms' + (ext ? ' ext' : '') + (expandable ? ' exp' : ''),
      })
    }
    /* Expand-in-place: a milestone with `decomposes_to` depth, when open, shows
     * its sub-process(es) + steps inline (the shared tree slice), recursively —
     * same model as the Sankey/Gantt. Tree nodes are 'tn_'+treeId. */
    const addDecomp = (mid: number) => {
      for (const v of visibleSubtree('n:' + mid)) {
        const cyId = 'tn_' + v.rec.id
        const parentCy = v.rec.parent === ('n:' + mid) ? ('m_' + mid) : ('tn_' + v.rec.parent)
        const lbl = (v.hasKids ? (v.open ? '▾ ' : '▸ ') : '') + v.rec.label
        els.push({
          data: {
            id: cyId, tid: v.rec.id, label: lbl, col: treeNodeColor(v.rec.kind, v.rec.state ?? null),
            h: Math.max(34, 20 + Math.ceil(v.rec.label.length / 28) * 13), expandable: v.hasKids ? 1 : 0,
          }, classes: 'tnode ' + (v.rec.kind === 'way' ? 'tway' : 'tstep'),
        })
        els.push({ data: { id: 'c_' + cyId, source: parentCy, target: cyId }, classes: 'contain' })
      }
    }
    for (const id of t.refs) addMs(id, false)
    for (let i = 0; i < t.refs.length - 1; i++) {
      if (model.M.get(t.refs[i]) && model.M.get(t.refs[i + 1]))
        els.push({ data: { id: 's_' + t.refs[i] + '_' + t.refs[i + 1], source: 'm_' + t.refs[i], target: 'm_' + t.refs[i + 1] }, classes: 'seq' })
    }
    for (const d of model.depEdges) {
      const f = inTrack.has(d.from_id), g = inTrack.has(d.to_id)
      if (!f && !g) continue
      if (!f) addMs(d.from_id, true); if (!g) addMs(d.to_id, true)
      els.push({ data: { id: 'd_' + d.from_id + '_' + d.to_id, source: 'm_' + d.from_id, target: 'm_' + d.to_id }, classes: 'dep' })
    }
    for (const id of t.refs) if (expansion.open.has('n:' + id)) addDecomp(id)
    cy.elements().remove(); cy.add(els)
    let lo
    try { lo = cy.layout({ name: 'dagre', rankDir: 'TB', nodeSep: 26, rankSep: 60, animate: false, nodeDimensionsIncludeLabels: true }) }
    catch { lo = cy.layout({ name: 'breadthfirst', directed: true, spacingFactor: 1.2, animate: false }) }
    runLayout(lo, () => fitReadable(0.72, 1.15))
    applyStateFilter()
  }

  function applyStateFilter() {
    if (!cy) return
    cy.nodes('.ms').forEach((n: { data: (k: string) => string; toggleClass: (c: string, b: boolean) => void }) => {
      n.toggleClass('dim', !activeStates.has(n.data('state')))
    })
    cy.edges().forEach((e: { source: () => { hasClass: (c: string) => boolean }; target: () => { hasClass: (c: string) => boolean }; toggleClass: (c: string, b: boolean) => void }) => {
      e.toggleClass('dim', e.source().hasClass('dim') || e.target().hasClass('dim'))
    })
  }

  function toggleState(s: string) {
    const next = new Set(activeStates)
    if (next.has(s)) next.delete(s); else next.add(s)
    activeStates = next
    applyStateFilter()
  }

  function applySearch() {
    if (!cy || !model) return
    const q = search.trim().toLowerCase()
    cy.nodes().removeClass('hit')
    if (!q) return
    cy.nodes('.ms').forEach((n: { data: (k: string) => number; addClass: (c: string) => void }) => {
      const m = model!.M.get(n.data('mid'))
      if (m && ((m.title || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q))) n.addClass('hit')
    })
    cy.nodes('.track').forEach((n: { data: (k: string) => string; addClass: (c: string) => void }) => {
      if ((n.data('slug') || '').includes(q)) n.addClass('hit')
    })
    const hits = cy.nodes('.hit')
    if (hits.nonempty()) cy.animate({ fit: { eles: hits, padding: 80 } }, { duration: 300 })
  }

  /* CODEX lens — render the capability graph (skills + processes/standards +
   * uses/requires/governed_by/verified_by/prescribes/decomposes_to), steps
   * collapsed to their process so shared skills show their forks; skill node
   * size ∝ audit runs (reputation). Reuses the cytoscape instance. */
  async function buildCodex() {
    if (!cy) return
    crumb = 'loading codex…'
    let g: CodexGraph
    try { g = await fetchCodexGraph() } catch (e) { crumb = 'codex: ' + (e as Error).message; return }
    const stepWay = new Map<number, number>()
    for (const w of g.ways) for (const sid of (w.node_refs || [])) stepWay.set(sid, w.id)
    const skillIds = new Set(g.skills.map((s) => s.id))
    const wayIds = new Set(g.ways.map((w) => w.id))
    const maxRuns = Math.max(1, ...g.skills.map((s) => s.runs || 0))
    const els: Record<string, unknown>[] = []
    for (const s of g.skills) {
      const runs = s.runs || 0
      els.push({ data: { id: 'sk_' + s.id, label: (s.ref || '').split('/').pop(), sz: 18 + Math.round(34 * (runs / maxRuns)) },
        classes: 'cskill role-' + (s.role || 'operator') })
    }
    for (const w of g.ways) {
      els.push({ data: { id: 'cw_' + w.id, label: w.name || (w.ref || '').split('/').pop() },
        classes: w.role === 'standard' ? 'cstandard' : 'cprocess' })
    }
    const resolve = (id: number): string | null =>
      skillIds.has(id) ? 'sk_' + id : wayIds.has(id) ? 'cw_' + id : stepWay.has(id) ? 'cw_' + stepWay.get(id) : null
    const seen = new Set<string>()
    const ecls: Record<string, string> = { uses: 'e-uses', requires: 'e-requires', governed_by: 'e-governed', verified_by: 'e-verified', prescribes: 'e-prescribes', decomposes_to: 'e-decomp' }
    for (const e of g.edges) {
      const cls = ecls[e.type]; if (!cls) continue
      const s = resolve(e.from_id), d = resolve(e.to_id)
      if (!s || !d || s === d) continue
      const k = cls + s + '>' + d; if (seen.has(k)) continue; seen.add(k)
      els.push({ data: { id: 'ce_' + els.length, source: s, target: d }, classes: cls })
    }
    cy.elements().remove(); cy.add(els)
    crumb = `codex · ${g.skills.length} skills · ${g.ways.length} processes/standards`
    let lo
    try { lo = cy.layout({ name: 'cose', animate: false, padding: 40, nodeRepulsion: 9000, idealEdgeLength: 90, nodeDimensionsIncludeLabels: true }) }
    catch { lo = cy.layout({ name: 'breadthfirst', directed: true, animate: false }) }
    runLayout(lo, () => cy.fit(undefined, 40))
  }

  function rebuildCurrentView() {
    if (mode === 'track' && curSlug) focusTrack(curSlug); else buildOverview()
    // keep an open milestone panel's data fresh
    if (milestonePanel.open && milestonePanel.milestone && model) {
      const fresh = model.M.get(milestonePanel.milestone.id)
      if (fresh) milestonePanel.milestone = fresh
    }
  }

  onMount(() => {
    cy = cytoscape({
      container: cyContainer,
      wheelSensitivity: 0.25,
      style: [
        { selector: 'node', style: { label: 'data(label)', color: '#e6edf3', 'font-size': 10, 'text-wrap': 'wrap', 'text-max-width': 130, 'text-valign': 'center', 'text-halign': 'center', 'border-width': 1.5, 'border-color': '#0d1117' } },
        { selector: 'node.track', style: { shape: 'round-rectangle', 'background-color': 'data(col)', width: 130, height: 48, 'font-size': 11, 'font-weight': 'bold', color: '#08111a', 'text-outline-width': 0, padding: '6px' } },
        { selector: 'node.ms', style: { shape: 'round-rectangle', 'background-color': 'data(col)', width: 210, height: 'data(h)', 'text-wrap': 'wrap', 'text-max-width': 198, 'text-valign': 'center', 'font-size': 10.5, color: '#08111a', 'text-outline-width': 0, padding: '5px' } },
        { selector: 'node.ext', style: { 'background-opacity': 0.25, 'border-width': 1, 'border-color': 'data(col)', color: '#8b949e', 'background-color': '#161b22' } },
        { selector: 'node.tnode', style: { shape: 'round-rectangle', 'background-color': 'data(col)', width: 168, height: 'data(h)', 'text-wrap': 'wrap', 'text-max-width': 156, 'font-size': 9.5, color: '#08111a', 'text-outline-width': 0, padding: '4px' } },
        { selector: 'node.tway', style: { 'border-width': 1.5, 'border-color': '#a371f7', 'background-opacity': 0.9, color: '#f3eefc' } },
        { selector: 'node.exp', style: { 'border-width': 2, 'border-color': '#39d0d8' } },
        { selector: 'node.dim', style: { opacity: 0.12 } },
        { selector: 'node.hit', style: { 'border-width': 3, 'border-color': '#39d0d8' } },
        { selector: 'edge', style: { 'curve-style': 'bezier', width: 1.4, 'line-color': '#30363d', 'target-arrow-shape': 'triangle', 'target-arrow-color': '#30363d', 'arrow-scale': 0.9 } },
        { selector: 'edge.dep', style: { 'line-color': '#f85149', 'target-arrow-color': '#f85149', width: 2 } },
        { selector: 'edge.seq', style: { 'line-color': '#283041', 'target-arrow-shape': 'none', 'line-style': 'dashed', width: 1 } },
        { selector: 'edge.contain', style: { 'line-color': '#3a4253', 'target-arrow-color': '#3a4253', width: 1.1 } },
        { selector: 'edge.dim', style: { opacity: 0.07 } },
        // CODEX lens — capability graph
        { selector: 'node.cskill', style: { shape: 'ellipse', width: 'data(sz)', height: 'data(sz)', label: 'data(label)', 'font-size': 9, color: '#c9d4e0', 'text-valign': 'bottom', 'text-margin-y': 2, 'background-color': '#5a6bd8', 'border-width': 0 } },
        { selector: 'node.cskill.role-check', style: { 'background-color': '#8957e5' } },
        { selector: 'node.cskill.role-capability', style: { 'background-color': '#3fb950', shape: 'hexagon' } },
        { selector: 'node.cprocess', style: { shape: 'round-rectangle', 'background-color': '#2f9bd8', width: 96, height: 24, padding: '4px', 'text-max-width': 88, 'text-wrap': 'wrap', label: 'data(label)', 'font-size': 9.5, color: '#08111a', 'font-weight': 'bold' } },
        { selector: 'node.cstandard', style: { shape: 'diamond', 'background-color': '#d29922', width: 30, height: 30, label: 'data(label)', 'font-size': 9, color: '#c9d4e0', 'text-valign': 'bottom', 'text-margin-y': 2 } },
        { selector: 'edge.e-uses', style: { 'line-color': '#3a4253', 'target-arrow-color': '#3a4253', width: 1.2 } },
        { selector: 'edge.e-requires', style: { 'line-color': '#db6d28', 'target-arrow-color': '#db6d28', 'line-style': 'dashed', width: 1.2 } },
        { selector: 'edge.e-governed', style: { 'line-color': '#d29922', 'target-arrow-color': '#d29922', width: 1.2 } },
        { selector: 'edge.e-verified', style: { 'line-color': '#8957e5', 'target-arrow-color': '#8957e5', width: 1.2 } },
        { selector: 'edge.e-prescribes', style: { 'line-color': '#8957e5', 'target-arrow-color': '#8957e5', 'line-style': 'dashed', width: 1 } },
        { selector: 'edge.e-decomp', style: { 'line-color': '#58a6ff', 'target-arrow-color': '#58a6ff', width: 1.4 } },
      // cytoscape's strict style types reject numeric width/height mappers; the
      // values are valid at runtime, so cast the stylesheet array.
      ] as never,
    })
    // milestone: expand-in-place if it has decomposition depth, else open detail
    cy.on('tap', 'node.ms', (evt: { target: { data: (k: string) => number } }) => {
      const mid = evt.target.data('mid')
      if (isExpandable('n:' + mid)) { toggleExpand('n:' + mid); if (curSlug) focusTrack(curSlug); return }
      const m = model?.M.get(mid); if (m) void selectMilestone(m)
    })
    // decomposition tree node (sub-process / step): expand if deeper, else detail
    cy.on('tap', 'node.tnode', (evt: { target: { data: (k: string) => string } }) => {
      const tid = evt.target.data('tid')
      if (isExpandable(tid)) { toggleExpand(tid); if (curSlug) focusTrack(curSlug); return }
      if (typeof tid === 'string' && tid.startsWith('n:')) {
        const rec = treeState.byId.get(tid)
        void selectMilestone({ id: Number(tid.slice(2)), title: rec?.label ?? '', state: rec?.state ?? null,
          project: null, description: null, completed_at: null, waiting_on: null, has_embedding: true })
      }
    })
    cy.on('tap', 'node.track', (evt: { target: { data: (k: string) => string } }) => focusTrack(evt.target.data('slug')))
    cy.on('tap', (evt: { target: unknown }) => { if (evt.target === cy) { closeMilestonePanel(); closeConnectionPanel() } })

    const onResize = () => cy && cy.resize()
    window.addEventListener('resize', onResize)

    // On phones the 320px rail would bury the canvas — start collapsed.
    if (isNarrow()) railOpen = false

    ;(async () => {
      await loadTaskGraph()
      void loadTree()  // decomposition depth for expand-in-place (DAG/Gantt/Sankey)
      if (taskGraph.data) buildOverview()
    })()

    // dev-only handle for headless verification of canvas-driven interactions
    if (import.meta.env.DEV) {
      ;(window as unknown as { __tg?: unknown }).__tg = {
        focusTrack, buildOverview,
        selectById: (id: number) => { const m = model?.M.get(id); if (m) void selectMilestone(m) },
      }
    }

    pollTimer = setInterval(async () => {
      const changed = await refreshIfChanged()
      if (changed) rebuildCurrentView()
    }, 15_000)

    return () => {
      if (pollTimer) clearInterval(pollTimer)
      window.removeEventListener('resize', onResize)
      if (cy) cy.destroy()
    }
  })

  function fmtTs(ts: string | null): string {
    if (!ts) return ''
    return String(ts).replace('T', ' ').slice(0, 16)
  }
</script>

<div class="tg-root">
  <!-- left rail -->
  <aside class="tg-rail" class:closed={!railOpen}>
    <div class="tg-head">
      <span class="tg-title">BOUSFIELD-DAG</span>
      <button class="tg-x" title="Collapse tracks panel" aria-label="Collapse tracks panel" onclick={() => toggleRail()}>✕</button>
    </div>
    <div class="tg-stamp">
      {#if taskGraph.loading}syncing…{:else if taskGraph.data}live · {fmtTs(taskGraph.data.generated_at)} UTC{:else if taskGraph.error}offline — {taskGraph.error}{:else}…{/if}
      <button class="tg-refresh" title="Refresh now" onclick={() => loadTaskGraph()}>⟳</button>
    </div>
    {#if taskGraph.data && taskGraph.data.tracks.length === 0}
      <!-- ticket #9: an empty space is not a broken view — say so -->
      <div class="tg-empty">No task graphs in this space yet. Task graphs are project tracks with milestones; a memory titled as a DAG
        shows in MEM, not here.</div>
    {/if}

    {#if spotTotal}
      <div class="tg-card">
        <div class="tg-card-label">Total — all tracks</div>
        <div class="tg-card-big">{spotTotal.done}<small>/{spotTotal.total} done · {spotTotal.pct}%</small></div>
        <div class="tg-bar">{#each segStyle(spotTotal) as seg (seg.s)}<i style="width:{seg.w}%;background:{COLOR[seg.s]}"></i>{/each}</div>
      </div>
    {/if}
    {#if spotClaims}
      <button class="tg-card tg-card-btn" onclick={() => focusTrack('claims-proof')}>
        <div class="tg-card-label">Claims-Proof Program</div>
        <div class="tg-card-big">{spotClaims.c.done}<small>/{spotClaims.c.total} done · {spotClaims.pct}%</small></div>
        <div class="tg-bar">{#each segStyle(spotClaims.c) as seg (seg.s)}<i style="width:{seg.w}%;background:{COLOR[seg.s]}"></i>{/each}</div>
      </button>
    {/if}
    {#if spotFlow}
      <button class="tg-card tg-card-btn" onclick={() => focusTrack('flow-runtime-evolution')}>
        <div class="tg-card-label">Node-RED Replacement · flow-runtime</div>
        <div class="tg-card-big">{spotFlow.c.done}<small>/{spotFlow.c.total} done · {spotFlow.pct}%</small></div>
        <div class="tg-bar">{#each segStyle(spotFlow.c) as seg (seg.s)}<i style="width:{seg.w}%;background:{COLOR[seg.s]}"></i>{/each}</div>
      </button>
    {/if}

    <div class="tg-section">State filter</div>
    <div class="tg-legend">
      {#each STATE_ORDER as s (s)}
        <button class="tg-leg" class:off={!activeStates.has(s)} onclick={() => toggleState(s)}>
          <i style="background:{COLOR[s]}"></i>{s} {model ? `(${model.totals[s] || 0})` : ''}
        </button>
      {/each}
    </div>

    <div class="tg-section">Tracks · click to focus</div>
    <input class="tg-search" placeholder="search milestones / tracks…" bind:value={search} oninput={applySearch} />
    <div class="tg-tracklist">
      {#each railTracks as t (t.way_id)}
        <button class="tg-trow" class:sel={curSlug === t.slug} onclick={() => focusTrack(t.slug)}>
          <span class="tg-minibar">{#each segStyle(t.c) as seg (seg.s)}<i style="width:{seg.w}%;background:{COLOR[seg.s]}"></i>{/each}</span>
          <span class="tg-tname" title={t.name}>{t.slug}</span>
          <span class="tg-tpct">{t.pct}%</span>
        </button>
      {/each}
    </div>
  </aside>

  <!-- canvas + topbar -->
  <div class="tg-main">
    <div class="tg-topbar">
      <button class="tg-btn tg-railtoggle" class:on={railOpen} onclick={() => toggleRail()}
        title={railOpen ? 'Hide tracks panel' : 'Show tracks panel'} aria-label="Toggle tracks panel">☰</button>
      <span class="tg-views">
        <button class:on={viewKind === 'graph'} onclick={() => { viewKind = 'graph'; setTimeout(() => { if (cy) { cy.resize(); rebuildCurrentView() } }, 50) }} title="Dependency graph">DAG</button>
        <button class:on={viewKind === 'gantt'} onclick={() => viewKind = 'gantt'} title="Schedule — the time dimension">GANTT</button>
        <button class:on={viewKind === 'sankey'} onclick={() => viewKind = 'sankey'} title="Flow — the distribution dimension">SANKEY</button>
      </span>
      <span class="tg-mode">{mode === 'track' ? 'TRACK' : 'OVERVIEW'}</span>
      <span class="tg-crumb">{crumb}</span>
      <button class="tg-btn" disabled={mode === 'overview'} onclick={buildOverview}>◂ Overview</button>
      <span class="tg-spacer"></span>
      <span class="tg-hint">{mode === 'track' ? 'red = dependency · dashed = sequence · faded = other-track' : 'click a track to expand its milestones'}</span>
      <button class="tg-btn" onclick={() => cy && cy.fit(undefined, 40)}>Fit</button>
    </div>
    <div class="tg-viewport">
      <div class="tg-canvas" bind:this={cyContainer} style:display={viewKind === 'graph' ? 'block' : 'none'}></div>
      {#if viewKind === 'gantt' && model}
        <GanttView {model} slug={curSlug} onpicktrack={focusTrack}
          onpickms={(id) => { const m = model?.M.get(id); if (m) void selectMilestone(m) }} />
      {/if}
      {#if viewKind === 'sankey'}
        <SankeyView {model} slug={curSlug} />
      {/if}
    </div>
  </div>

  <!-- milestone detail + live NEXUS memories -->
  {#if milestonePanel.open && milestonePanel.milestone}
    {@const m = milestonePanel.milestone}
    <aside class="tg-detail">
      <button class="tg-detail-x" onclick={closeMilestonePanel}>✕</button>
      <div class="tg-detail-id">milestone #{m.id} · {m.project ?? '—'}</div>
      <div class="tg-detail-title">{m.title}</div>
      <span class="tg-badge" style="background:{COLOR[m.state || 'planned']}">{m.state}</span>
      <div class="tg-detail-desc">{m.description || '(no description)'}</div>
      {#if m.completed_at}<div class="tg-detail-meta">completed: {m.completed_at}</div>{/if}

      <div class="tg-mem-head">RELATED NEXUS MEMORIES <span class="tg-mem-sub">(semantic · live)</span></div>
      {#if milestonePanel.memLoading}
        <div class="tg-mem-status">searching memory…</div>
      {:else if milestonePanel.memError}
        <div class="tg-mem-status err">{milestonePanel.memError}</div>
      {:else if milestonePanel.memories.length === 0}
        <div class="tg-mem-status">no related memories</div>
      {:else}
        {#each milestonePanel.memories as mem (mem.id)}
          <div class="tg-mem">
            <div class="tg-mem-top">
              <span class="tg-mem-sim">{(mem.similarity * 100).toFixed(0)}%</span>
              <span class="tg-mem-topic">{mem.topic || `#${mem.id}`}</span>
              {#if mem.importance != null}<span class="tg-mem-imp">i{mem.importance}</span>{/if}
            </div>
            <div class="tg-mem-body">{mem.content}</div>
          </div>
        {/each}
      {/if}
    </aside>
  {/if}

  <!-- connection (flow-edge) detail — skill / procedure / audit metadata -->
  {#if connectionPanel.open}
    <aside class="tg-detail">
      <button class="tg-detail-x" onclick={closeConnectionPanel}>✕</button>
      <div class="tg-detail-id">CONNECTION{#if connectionPanel.edge?.relationLabel} · {connectionPanel.edge.relationLabel}{/if}</div>
      {#if connectionPanel.loading}
        <div class="tg-mem-status">loading connection…</div>
      {:else if connectionPanel.error}
        <div class="tg-mem-status err">{connectionPanel.error}</div>
      {:else if connectionPanel.edge}
        {@const e = connectionPanel.edge}
        <div class="tg-conn-flow">
          {#if e.from}<span class="tg-conn-from">{e.from.label}</span><span class="tg-conn-arrow">→</span>{/if}
          <span class="tg-detail-title tg-conn-to">{e.to.label}</span>
        </div>
        <div class="tg-conn-tags">
          <span class="tg-conn-kind">{e.to.wayKind ?? e.to.graphKind ?? e.to.kind}</span>
          {#if e.to.state}<span class="tg-badge" style="background:{COLOR[e.to.state] || COLOR.planned}">{e.to.state}</span>{/if}
          {#if e.to.gate}<span class="tg-conn-gate" class:approval={e.to.gate === 'requires-approval'}>{e.to.gate}</span>{/if}
        </div>

        {#if e.to.skill}
          <div class="tg-conn-sec">SKILL</div>
          <div class="tg-conn-row"><span class="tg-conn-rt">{e.to.skill.runtime ?? '—'}</span><code class="tg-conn-ref">{e.to.skill.ref ?? ''}</code></div>
        {/if}
        {#if e.to.check}
          <div class="tg-conn-sec">CHECK <span class="tg-conn-subtle">({e.to.check.kind ?? '—'})</span></div>
          <div class="tg-conn-assert">{e.to.check.assert ?? ''}</div>
        {/if}

        <div class="tg-mem-head">AUDIT RECEIPTS <span class="tg-mem-sub">({e.audits.length})</span></div>
        {#if e.audits.length === 0}
          <div class="tg-mem-status">no audit receipts yet</div>
        {:else}
          {#each e.audits as a, i (i)}
            <div class="tg-mem">
              <div class="tg-mem-top">
                <span class="tg-conn-result" class:pass={a.result === 'pass'} class:fail={a.result === 'fail'}>{a.result ?? '—'}</span>
                <span class="tg-mem-topic">{a.action_ref ?? ''}</span>
                {#if a.run_id}<span class="tg-mem-imp">{a.run_id}</span>{/if}
              </div>
              <div class="tg-conn-aud-meta">{a.actor ?? ''}{#if a.at} · {fmtTs(a.at)}{/if}</div>
              {#if a.evidence}<div class="tg-mem-body">{JSON.stringify(a.evidence)}</div>{/if}
            </div>
          {/each}
        {/if}
      {/if}
    </aside>
  {/if}
</div>

<style>
  .tg-root { position: absolute; inset: 0; display: flex; background: #0d1117; color: #e6edf3;
    font-family: var(--font-mono, ui-monospace, monospace); }
  .tg-rail { width: 320px; min-width: 320px; height: 100%; overflow-y: auto; background: #12161d;
    border-right: 1px solid #2a313c; padding: 12px 12px 32px; box-sizing: border-box; }
  .tg-rail.closed { display: none; }
  .tg-head { display: flex; align-items: center; }
  .tg-title { font-size: 12px; letter-spacing: 0.1em; font-weight: 700; flex: 1; }
  .tg-x, .tg-detail-x { background: none; border: 1px solid #2a313c; color: #8b949e; cursor: pointer;
    width: 22px; height: 22px; border-radius: 4px; font-size: 12px; line-height: 1; }
  .tg-x:hover, .tg-detail-x:hover { border-color: #39d0d8; color: #39d0d8; }
  .tg-stamp { font-size: 10px; color: #6e7681; margin: 4px 0 12px; display: flex; align-items: center; gap: 6px; }
  .tg-refresh { margin-left: auto; background: none; border: 1px solid #2a313c; color: #8b949e;
    border-radius: 4px; cursor: pointer; font-size: 11px; padding: 0 5px; }
  .tg-refresh:hover { border-color: #39d0d8; color: #39d0d8; }

  .tg-card { background: #1a212c; border: 1px solid #2a313c; border-radius: 8px; padding: 9px 10px;
    margin-bottom: 8px; width: 100%; text-align: left; display: block; }
  .tg-card-btn { cursor: pointer; }
  .tg-card-btn:hover { border-color: #39d0d8; }
  .tg-card-label { font-size: 9.5px; letter-spacing: 0.12em; color: #6e7681; text-transform: uppercase; }
  .tg-card-big { font-size: 21px; font-weight: 700; margin-top: 1px; color: #e6edf3; }
  .tg-card-big small { font-size: 11px; color: #6e7681; font-weight: 500; }
  .tg-bar, .tg-minibar { height: 6px; border-radius: 3px; background: #0d1117; overflow: hidden; display: flex; margin-top: 7px; }
  .tg-bar i, .tg-minibar i { display: block; height: 100%; }

  .tg-section { font-size: 9.5px; letter-spacing: 0.12em; color: #6e7681; text-transform: uppercase; margin: 14px 0 6px; }
  .tg-legend { display: flex; flex-wrap: wrap; gap: 5px; }
  .tg-leg { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; color: #8b949e;
    background: none; border: 1px solid #232a34; border-radius: 4px; padding: 3px 6px; cursor: pointer; text-transform: capitalize; }
  .tg-leg i { width: 8px; height: 8px; border-radius: 2px; }
  .tg-leg.off { opacity: 0.4; text-decoration: line-through; }

  .tg-search { width: 100%; padding: 6px 8px; background: #0d1117; border: 1px solid #2a313c; border-radius: 5px;
    color: #e6edf3; font-size: 11px; margin-bottom: 8px; box-sizing: border-box; font-family: inherit; }
  .tg-search:focus { outline: none; border-color: #39d0d8; }

  .tg-tracklist { display: flex; flex-direction: column; gap: 1px; }
  .tg-trow { display: flex; align-items: center; gap: 7px; padding: 5px 6px; border-radius: 5px;
    cursor: pointer; background: none; border: none; color: inherit; width: 100%; text-align: left; }
  .tg-trow:hover { background: #1a212c; }
  .tg-trow.sel { background: #15324a; }
  .tg-minibar { width: 44px; min-width: 44px; margin-top: 0; }
  .tg-tname { flex: 1; min-width: 0; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .tg-tpct { font-size: 10px; color: #6e7681; width: 28px; text-align: right; }

  .tg-main { flex: 1; height: 100%; display: flex; flex-direction: column; min-width: 0; }
  .tg-topbar { height: 42px; min-height: 42px; display: flex; align-items: center; gap: 9px;
    padding: 0 12px; border-bottom: 1px solid #2a313c; background: #12161d; }
  .tg-mode { font-size: 10px; letter-spacing: 0.1em; color: #6e7681; }
  .tg-crumb { font-weight: 600; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 38%; }
  .tg-spacer { flex: 1; }
  .tg-hint { color: #6e7681; font-size: 10px; }
  .tg-btn { background: #1a212c; border: 1px solid #2a313c; color: #e6edf3; border-radius: 5px;
    padding: 4px 10px; font-size: 11px; cursor: pointer; font-family: inherit; }
  .tg-btn:hover { border-color: #39d0d8; }
  .tg-railtoggle { flex-shrink: 0; padding: 4px 9px; }
  .tg-railtoggle.on { border-color: #39d0d8; color: #39d0d8; }
  .tg-views { display: inline-flex; border: 1px solid #2a313c; border-radius: 5px; overflow: hidden; flex-shrink: 0; }
  .tg-views button { font: inherit; font-size: 10px; letter-spacing: 0.06em; padding: 4px 9px; background: transparent;
    border: none; border-left: 1px solid #2a313c; color: #6e7681; cursor: pointer; }
  .tg-views button:first-child { border-left: none; }
  .tg-views button:hover { color: #39d0d8; }
  .tg-views button.on { background: #15324a; color: #39d0d8; }
  .tg-btn[disabled] { opacity: 0.4; cursor: default; }
  .tg-viewport { position: relative; flex: 1; min-height: 0; width: 100%; }
  .tg-canvas { position: absolute; inset: 0; }

  .tg-detail { position: absolute; right: 12px; bottom: 12px; top: 54px; width: 340px;
    overflow-y: auto; background: #12161d; border: 1px solid #2a313c; border-radius: 10px;
    padding: 13px; box-shadow: 0 10px 40px rgba(0,0,0,0.5); box-sizing: border-box; }
  .tg-detail-x { position: absolute; top: 10px; right: 10px; }
  .tg-detail-id { font-size: 9.5px; letter-spacing: 0.08em; color: #6e7681; padding-right: 26px; }
  .tg-detail-title { font-size: 13.5px; font-weight: 700; margin: 3px 0 8px; line-height: 1.3; }
  .tg-badge { display: inline-block; font-size: 9.5px; letter-spacing: 0.1em; text-transform: uppercase;
    padding: 2px 8px; border-radius: 20px; font-weight: 700; color: #08111a; }
  .tg-detail-desc { margin-top: 9px; color: #c9d4e0; line-height: 1.5; font-size: 11.5px; white-space: pre-wrap; }
  .tg-detail-meta { margin-top: 8px; font-size: 10px; color: #6e7681; }

  .tg-mem-head { margin-top: 14px; padding-top: 10px; border-top: 1px solid #232a34;
    font-size: 9.5px; letter-spacing: 0.1em; color: #8b949e; text-transform: uppercase; }
  .tg-mem-sub { color: #4d8; opacity: 0.7; text-transform: none; letter-spacing: 0; }
  .tg-mem-status { font-size: 11px; color: #6e7681; margin-top: 8px; }
  .tg-mem-status.err { color: #f85149; }
  .tg-mem { margin-top: 8px; padding: 7px 8px; background: #0f141b; border: 1px solid #232a34; border-radius: 6px; }
  .tg-mem-top { display: flex; align-items: center; gap: 6px; }
  .tg-mem-sim { font-size: 10px; font-weight: 700; color: #3fb950; }
  .tg-mem-topic { font-size: 10.5px; color: #b9c4d0; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .tg-mem-imp { font-size: 9px; color: #6e7681; }
  .tg-mem-body { font-size: 10.5px; color: #8b949e; margin-top: 4px; line-height: 1.45; max-height: 64px; overflow: hidden; }

  /* connection (flow-edge) detail */
  .tg-conn-flow { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; margin: 4px 0 6px; padding-right: 24px; }
  .tg-conn-from { font-size: 10.5px; color: #8b949e; }
  .tg-conn-arrow { color: #39d0d8; }
  .tg-conn-to { margin: 0; }
  .tg-conn-tags { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 4px; }
  .tg-conn-kind { font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #8b949e; border: 1px solid #2a313c; border-radius: 4px; padding: 1px 6px; }
  .tg-conn-gate { font-size: 9px; letter-spacing: 0.06em; color: #3fb950; border: 1px solid #233; border-radius: 10px; padding: 1px 7px; }
  .tg-conn-gate.approval { color: #d29922; }
  .tg-conn-sec { margin-top: 12px; font-size: 9.5px; letter-spacing: 0.1em; color: #6e7681; text-transform: uppercase; }
  .tg-conn-subtle { color: #4d545d; text-transform: none; letter-spacing: 0; }
  .tg-conn-row { display: flex; align-items: center; gap: 7px; margin-top: 4px; }
  .tg-conn-rt { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #8957e5; border: 1px solid #2a313c; border-radius: 4px; padding: 1px 6px; }
  .tg-conn-ref { font-size: 11px; color: #c9d4e0; background: #0f141b; padding: 1px 6px; border-radius: 4px; word-break: break-all; }
  .tg-conn-assert { margin-top: 4px; font-size: 11px; color: #c9d4e0; line-height: 1.4; }
  .tg-conn-result { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #6e7681; }
  .tg-conn-result.pass { color: #3fb950; }
  .tg-conn-result.fail { color: #f85149; }
  .tg-conn-aud-meta { font-size: 9.5px; color: #6e7681; margin-top: 2px; }

  /* Mobile: the rail overlays the canvas (instead of squeezing it to nothing)
   * and the detail panel goes full-width so memories stay readable. The ☰
   * toggle in the topbar opens/closes the rail; ✕ inside it collapses it. */
  @media (max-width: 760px) {
    .tg-rail {
      position: absolute; z-index: 40; left: 0; top: 0; bottom: 0;
      width: min(300px, 86vw);
      box-shadow: 6px 0 24px rgba(0, 0, 0, 0.55);
    }
    .tg-hint { display: none; }
    .tg-crumb { max-width: 46vw; }
    .tg-detail { left: 10px; right: 10px; width: auto; top: auto; max-height: 52%; }
  }
  .tg-empty { padding: 10px 12px; font-size: 12px; line-height: 1.45; color: var(--on-surface-variant, #8b949e); border-bottom: 1px solid var(--outline-variant, #30363d); }
</style>
